import type { Question } from '@/types';

/**
 * Focuses the field of a text/textarea slide once the visitor navigates onto it (A/B test `autofocusInput`).
 *
 * The next slide's field only becomes reachable after the slide animation, but iOS Safari opens the on-screen
 * keyboard only when focus() runs inside the tap that triggered the navigation. So the navigation handler
 * focuses an invisible primer input right away (keyboard opens), and the arriving slide hands the focus over
 * to its real field once the animation is done — moving focus between inputs keeps the keyboard open.
 *
 * A pending primer is also the signal that the visitor navigated, so restoring a persisted slide or the initial
 * page load never steals focus.
 */

const FIELD_SELECTOR = 'input:not([type="hidden"]), textarea';
// Releases the primer when no slide claimed it, so the keyboard does not stay open over nothing.
const PRIMER_TIMEOUT_MS = 1500;

let primer: HTMLInputElement | null = null;
let primerTimeout = 0;

const createPrimer = (): HTMLInputElement => {
  const input = document.createElement('input');
  input.type = 'text';
  input.tabIndex = -1;
  input.setAttribute('aria-hidden', 'true');
  // 16px keeps iOS from zooming in on focus; opacity instead of display:none since hidden inputs can't focus.
  input.style.cssText =
    'position:fixed;top:0;left:0;width:1px;height:1px;padding:0;border:0;opacity:0;font-size:16px;pointer-events:none;';
  document.body.appendChild(input);
  return input;
};

export const isAutofocusSlide = (slide?: Question): boolean => slide?.type === 'text' || slide?.type === 'textarea';

export const isPrimed = (): boolean => primer !== null;

export const release = (): void => {
  clearTimeout(primerTimeout);
  primer?.blur();
  primer?.remove();
  primer = null;
};

/**
 * Must be called synchronously from the click/tap handler that navigates to `slide`.
 */
export const prime = (slide: Question | undefined, inputMode?: Question['inputMode']): void => {
  if (!isAutofocusSlide(slide)) return;
  if (!primer) primer = createPrimer();
  // Same keyboard as the target field, so it doesn't switch layout on hand-over.
  if (inputMode) primer.setAttribute('inputmode', inputMode);
  else primer.removeAttribute('inputmode');
  primer.focus({ preventScroll: true });
  clearTimeout(primerTimeout);
  primerTimeout = window.setTimeout(release, PRIMER_TIMEOUT_MS);
};

/**
 * Moves the focus from the primer to the first field inside `container`.
 */
export const handOver = (container: HTMLElement | null): void => {
  const field = container?.querySelector<HTMLInputElement | HTMLTextAreaElement>(FIELD_SELECTOR);
  if (field) {
    field.focus({ preventScroll: true });
    const end = field.value.length;
    // Caret behind existing text when the visitor comes back to an answered slide.
    field.setSelectionRange?.(end, end);
  }
  // After the field took the focus, so the keyboard never closes in between.
  primer?.remove();
  primer = null;
  clearTimeout(primerTimeout);
};
